import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';

import type { AuthReqBodyDto, AuthResBodyDto } from '@repo/schema-api-customer-hub';
import type { SubscribedCustomerTokenPayloadDto } from '@repo/schema-api-partner';

import { customerHubApiClient } from '@/libs/customer-hub-api-client.service';
import type { LoginMethod } from '@/shared/services/session.service';
import { clearSession, getStoredToken, storeSession } from '@/shared/services/session.service';

export type TokenPayload = SubscribedCustomerTokenPayloadDto & { iat: number; exp: number };

export class LoginError extends Error {
  public constructor(
    message: string,
    public readonly challenge?: AuthResBodyDto['data']['challenge']
  ) {
    super(message);
    this.name = 'LoginError';
  }
}

export function validateToken(token: string): TokenPayload {
  const payload = jwtDecode<TokenPayload>(token);

  if (payload.exp * 1000 < Date.now()) {
    throw new Error('Token has expired');
  }

  return payload;
}

export function storeToken(token: string, loginMethod: LoginMethod): TokenPayload {
  const payload = validateToken(token);

  storeSession(token, loginMethod);
  customerHubApiClient.setAuthorization(token);
  Sentry.setUser({ email: payload.email, username: payload.name });

  return payload;
}

export function logout(): void {
  clearSession();
  Sentry.setUser(null);
}

function resolveLoginErrorMessage(result: AuthResBodyDto['data']): string {
  if (result.locked) {
    return 'login.form.errors.tooManyAttempts';
  }

  if (result.noActiveContracts) {
    return 'login.form.errors.noActiveContracts';
  }

  return 'login.form.errors.bookingNotFound';
}

export async function login(data: AuthReqBodyDto): Promise<TokenPayload> {
  const result = await customerHubApiClient.authenticate(data);

  if (!result.auth || result.token === undefined) {
    throw new LoginError(resolveLoginErrorMessage(result), result.challenge);
  }

  return storeToken(result.token, 'form');
}

export function loginWithToken(token: string): TokenPayload {
  return storeToken(token, 'link');
}

export function initializeAuth(): void {
  const token = getStoredToken();

  if (!token) {
    return;
  }

  try {
    const payload = validateToken(token);

    customerHubApiClient.setAuthorization(token);
    Sentry.setUser({ email: payload.email, username: payload.name });
  } catch (error) {
    Sentry.captureMessage('Stored token is invalid, logging out', {
      level: 'warning',
      extra: { reason: error instanceof Error ? error.message : 'unknown' },
    });
    logout();
  }
}
